import {ComponentChildren} from "preact";
import {Signal} from "@preact/signals";
import {Dialog, DialogClose, DialogContent} from "./dialog";
import {Card} from "./card";
import {mdiAlertCircleOutline, mdiAlertOutline, mdiHelpRhombusOutline, mdiInformationOutline} from "@mdi/js";
import Icon from "@mdi/react";
import {FormFooter} from "./form";
import {AccentType} from "../../theme/accents";

export type MessageType = "INFO" | "WARNING" | "CONFIRM" | "ALERT";
export type MessageButton = [string, string];

export type Alert = {
    type: MessageType;
    buttons: MessageButton[];
    title: string;
    message: ComponentChildren;
    resolve: (button: string) => void;
    open: boolean;
};

export namespace Buttons {
    export const OK: MessageButton = ["OK", "ok"];
    export const Cancel: MessageButton = ["Cancel", "cancel"];
    export const Yes: MessageButton = ["Yes", "yes"];
    export const No: MessageButton = ["No", "no"];
}

const s = new Signal<Alert[]>([]);

export function useAlert() {
    const newAlert = (alert: Alert) => {
        s.value = [alert, ...s.value];
    }

    const _showGeneric = (type: MessageType, title: string, message: ComponentChildren, buttons: MessageButton[]): Promise<string> => {
        return new Promise((resolve) => {
            newAlert({type, buttons, title, message, resolve, open: true});
        });
    }

    const showInfo = (title: string, message: ComponentChildren, buttons: MessageButton[] = [Buttons.OK]): Promise<string> => _showGeneric("INFO", title, message, buttons);
    const showWarning = (title: string, message: ComponentChildren, buttons: MessageButton[] = [Buttons.OK]): Promise<string> => _showGeneric("WARNING", title, message, buttons);
    const showConfirm = (title: string, message: ComponentChildren, buttons: MessageButton[] = [Buttons.Yes, Buttons.No]): Promise<string> => _showGeneric("CONFIRM", title, message, buttons);
    const showAlert = (title: string, message: ComponentChildren, buttons: MessageButton[] = [Buttons.OK]): Promise<string> => _showGeneric("ALERT", title, message, buttons);

    return {
        showInfo, showAlert, showWarning, showConfirm,
    };
}

export function AlertContainer() {
    return <>{s.value.map(alert =>
        <Dialog
            open={alert.open}
            onOpenChange={(open: boolean) => {
                if (!open) {
                    alert.open = false;
                    s.value = s.value.filter(a => a !== alert);
                }
            }}
            resolve={(reason: string) => {
                alert.resolve(reason);
            }}
        >
            <DialogContent>
                <Card
                    accent={({"ALERT": "danger", "INFO": "default", "WARNING": "warning"} as {[key: string]: AccentType})[alert.type]}
                    title={alert.title}
                    footer={<FormFooter>{alert.buttons.map(button => <DialogClose reason={button[1]}>{button[0]}</DialogClose>)}</FormFooter>}
                >
                    <div style={{display: "flex", alignItems: "center", gap: ".5rem", paddingLeft: ".5rem", paddingRight: ".5rem"}}>
                        <Icon path={{
                            "INFO": mdiInformationOutline,
                            "WARNING": mdiAlertCircleOutline,
                            "CONFIRM": mdiHelpRhombusOutline,
                            "ALERT": mdiAlertOutline,
                        }[alert.type]} size="2em" />
                        <p>
                            {alert.message}
                        </p>
                    </div>
                </Card>
            </DialogContent>
        </Dialog>
    )}</>;
}
